import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card, Text, ActionIcon, Group, Box, TextInput, Popover } from '@mantine/core';
import { Palette, Trash2, Calendar, GripVertical, ClipboardList } from 'lucide-react';
import { useDebouncedCallback } from '@mantine/hooks';
import { useUpdateNote, useDeleteNote } from '../../hooks/useNotes';
import { Note } from '../../types/notes';
import { todoApi } from '../../api/todoApi';
import { useUser } from '../../contexts/UserContext';
import { ColorPicker } from './ColorPicker';
import { ConvertToTaskDialog } from './ConvertToTaskDialog';
import { NoteRichTextEditor } from './NoteRichTextEditor';

interface NoteCardProps {
  note: Note;
  onDelete?: (id: number) => void;
  style?: React.CSSProperties;
  showControls?: boolean;
  isDraggable?: boolean;
  onMouseDown?: (e: React.MouseEvent) => void;
  shouldFocus?: boolean;
  onFocused?: () => void;
}

// Utility function to determine if a color is light or dark
const isLightColor = (hexColor: string): boolean => {
  if (!hexColor) return true; // Default to light for no color
  
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate luminance using the relative luminance formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return true if light (luminance > 0.6 means it's bright)
  return luminance > 0.6;
};

// Function to darken a hex color for dark mode
const darkenColorForDarkMode = (hexColor: string, amount: number = 0.6): string => {
  if (!hexColor) return '#2a2a2a'; // Default dark color
  
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Darken each component
  const darkR = Math.floor(r * amount);
  const darkG = Math.floor(g * amount);
  const darkB = Math.floor(b * amount);
  
  // Convert back to hex
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(darkR)}${toHex(darkG)}${toHex(darkB)}`;
};

// Detect if user is in dark mode
const isDarkMode = (): boolean => {
  // Check if the document has dark theme class or data attribute
  const html = document.documentElement;
  return html.classList.contains('theme-dark') || 
         html.getAttribute('data-effective-theme') === 'dark' ||
         (!html.classList.contains('theme-light') && 
          window.matchMedia('(prefers-color-scheme: dark)').matches);
};

// Get the appropriate background and text colors based on theme
const getThemeAwareColors = (originalColor: string) => {
  const isDark = isDarkMode();
  
  if (isDark) {
    // In dark mode: use darker version of the color with white text
    return {
      backgroundColor: darkenColorForDarkMode(originalColor),
      textColor: '#f8f9fa',
      mutedColor: '#e9ecef',
      iconColor: '#dee2e6'
    };
  } else {
    // In light mode: use original color with appropriate contrast text
    const isLight = isLightColor(originalColor);
    return {
      backgroundColor: originalColor,
      textColor: isLight ? '#1a1a1a' : '#f8f9fa',
      mutedColor: isLight ? '#4a4a4a' : '#e9ecef',
      iconColor: isLight ? '#6a6a6a' : '#dee2e6'
    };
  }
};

export const NoteCard = ({ note, onDelete, style, showControls = true, isDraggable = false, onMouseDown, shouldFocus, onFocused }: NoteCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localTitle, setLocalTitle] = useState(note.title || '');
  const [localContent, setLocalContent] = useState(note.content);
  const [showCreatedDate, setShowCreatedDate] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [themeChanged, setThemeChanged] = useState(0); // Force re-render on theme change
  
  const { currentUser } = useUser();
  
  const updateNoteMutation = useUpdateNote();
  const deleteNoteMutation = useDeleteNote();
  
  const cardRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  // Auto-save with debouncing
  const debouncedSave = useDebouncedCallback(
    useCallback((updates: Partial<Note>) => {
      updateNoteMutation.mutate({ id: note.id, data: updates });
    }, [note.id, updateNoteMutation]),
    800 // 800ms delay
  );

  // Save title changes
  useEffect(() => {
    if (localTitle !== (note.title || '')) {
      debouncedSave({ title: localTitle });
    }
  }, [localTitle, note.title, debouncedSave]);

  // Save content changes
  useEffect(() => {
    if (localContent !== note.content) {
      debouncedSave({ content: localContent });
    }
  }, [localContent, note.content, debouncedSave]);

  // Listen for theme changes
  useEffect(() => {
    const handleThemeChange = () => {
      setThemeChanged(prev => prev + 1);
    };

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', handleThemeChange);

    // Listen for manual theme changes via MutationObserver
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'class' || mutation.attributeName === 'data-effective-theme')) {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-effective-theme']
    });

    return () => {
      mediaQuery.removeEventListener('change', handleThemeChange);
      observer.disconnect();
    };
  }, []);

  // Handle focus request from parent
  useEffect(() => {
    if (shouldFocus) {
      setIsEditing(true);
      setTimeout(() => {
        if (titleRef.current) {
          titleRef.current.focus();
          titleRef.current.select(); // Select all text for easy replacement
        }
        onFocused?.(); // Notify parent that focus has been handled
      }, 100);
    }
  }, [shouldFocus, onFocused]);

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      deleteNoteMutation.mutate(note.id);
      onDelete?.(note.id);
    }
  };

  const handleColorChange = (newColor: string) => {
    updateNoteMutation.mutate({ 
      id: note.id, 
      data: { color: newColor } 
    });
    setColorPickerOpen(false);
  };

  const handleConvertToTask = async (listId: number, deleteNote: boolean) => {
    try {
      if (!currentUser?.email) {
        throw new Error('User not authenticated');
      }

      // Create the todo item
      const taskData = {
        title: localTitle || 'Untitled Task',
        description: localContent,
        createdBy: currentUser.email,
        priority: 'Medium' as const,
        listId: listId
      };

      await todoApi.createTodo(taskData, currentUser.email);

      // Delete the note if requested
      if (deleteNote) {
        deleteNoteMutation.mutate(note.id);
        onDelete?.(note.id);
      }

      // Show success message (you might want to add a toast notification here)
      console.log('Note converted to task successfully');
    } catch (error) {
      console.error('Failed to convert note to task:', error);
      // You might want to show an error toast here
      throw error;
    }
  };

  const handleCardClick = () => {
    if (!isEditing) {
      setIsEditing(true);
      setTimeout(() => {
        if (titleRef.current) {
          titleRef.current.focus();
        }
      }, 100);
    }
  };

  // Enhanced blur handling to support tab navigation between title and content
  const handleBlur = (e: React.FocusEvent) => {
    // Check if the focus is moving to another element within the same note
    const relatedTarget = e.relatedTarget as HTMLElement;
    const currentCard = cardRef.current;
    
    if (currentCard && relatedTarget) {
      // If focus is moving to another input within the same card, don't exit editing mode
      if (currentCard.contains(relatedTarget)) {
        return;
      }
    }
    
    // Only exit editing mode if focus is leaving the entire note
    setIsEditing(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  // Handle tab navigation between title and content
  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      // Focus will be handled by the rich text editor
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsEditing(false);
      // Remove focus from the input
      if (titleRef.current) {
        titleRef.current.blur();
      }
    }
  };

  const handleContentKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      if (titleRef.current) {
        titleRef.current.focus();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsEditing(false);
    }
  };

  // Get theme-aware colors for the note (recalculated when theme changes)
  const themeColors = React.useMemo(() => {
    return getThemeAwareColors(note.color || '#ffeb9c'); // Default to light yellow
  }, [note.color, themeChanged]);

  return (
    <>
      <Card
      ref={cardRef}
      shadow="sm"
      padding="md"
      radius="md"
      style={{
        backgroundColor: themeColors.backgroundColor,
        width: note.width || 250,
        minHeight: note.height || 200,
        cursor: 'default',
        position: 'relative',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        transition: 'all 0.2s ease',
        zIndex: 1,
        // CSS custom properties for theme-aware colors
        '--note-text-color': themeColors.textColor,
        '--note-text-muted': themeColors.mutedColor,
        '--note-icon-color': themeColors.iconColor,
        ...style
      } as React.CSSProperties}
      onMouseEnter={() => setShowCreatedDate(true)}
      onMouseLeave={() => setShowCreatedDate(false)}
      onClick={handleCardClick}
    >

      {/* Actions - Always reserve space, only visible on hover */}
      {showControls && (
        <Group
          gap="xs"
          justify="flex-end"
          style={{
            position: 'absolute',
            top: 2,
            right: 2,
            zIndex: 15,
            opacity: showCreatedDate ? 1 : 0,
            transition: 'opacity 0.2s ease'
          }}
        >
          {/* Color Picker */}
          <Popover
            opened={colorPickerOpen}
            onClose={() => setColorPickerOpen(false)}
            position="bottom"
            withArrow
          >
            <Popover.Target>
              <ActionIcon
                size="sm"
                variant="subtle"
                onClick={(e) => {
                  e.stopPropagation();
                  setColorPickerOpen(!colorPickerOpen);
                }}
                style={{
                  width: '24px',
                  height: '24px',
                  backgroundColor: 'transparent'
                }}
                title="Change color"
              >
                <Palette size={14} style={{ color: 'var(--note-icon-color)' }} />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
              <ColorPicker
                currentColor={note.color}
                onColorChange={handleColorChange}
                size="sm"
              />
            </Popover.Dropdown>
          </Popover>

          {/* Convert to Task Button */}
          <ActionIcon
            size="sm"
            variant="subtle"
            onClick={(e) => {
              e.stopPropagation();
              setShowConvertDialog(true);
            }}
            style={{
              width: '24px',
              height: '24px',
              backgroundColor: 'transparent'
            }}
            title="Convert to task"
          >
            <ClipboardList size={14} style={{ color: 'var(--note-icon-color)' }} />
          </ActionIcon>

          {/* Delete Button */}
          <ActionIcon
            size="sm"
            variant="subtle"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
            style={{
              width: '24px',
              height: '24px',
              backgroundColor: 'transparent'
            }}
            title="Delete note"
          >
            <Trash2 size={14} color="rgba(220, 53, 69, 0.8)" />
          </ActionIcon>

          {/* Drag Handle - Only in free mode */}
          {isDraggable && (
            <ActionIcon
              size="sm"
              variant="subtle"
              style={{
                width: '24px',
                height: '24px',
                backgroundColor: 'transparent',
                cursor: 'grab'
              }}
              onMouseDown={onMouseDown}
            >
              <GripVertical size={14} style={{ color: 'var(--note-icon-color)' }} />
            </ActionIcon>
          )}
        </Group>
      )}

      {/* Title */}
      <Box mt="sm" mb="xs">
        {isEditing ? (
          <TextInput
            ref={titleRef}
            value={localTitle}
            onChange={(e) => setLocalTitle(e.currentTarget.value)}
            onBlur={handleBlur}
            onKeyDown={handleTitleKeyDown}
            placeholder="Note title..."
            variant="unstyled"
            size="sm"
            styles={{
              input: {
                fontWeight: 600,
                fontSize: '16px',
                color: 'var(--note-text-color, var(--app-text-primary))',
                backgroundColor: 'transparent',
                padding: 0,
                border: 'none',
                '&:focus': {
                  outline: 'none'
                }
              }
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <Text
            size="sm"
            fw={600}
            style={{
              color: 'var(--note-text-color, var(--app-text-primary))',
              minHeight: '20px',
              cursor: 'text'
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!isEditing) {
                setIsEditing(true);
                setTimeout(() => {
                  if (titleRef.current) {
                    titleRef.current.focus();
                  }
                }, 100);
              }
            }}
          >
            {localTitle || 'Untitled Note'}
          </Text>
        )}
      </Box>

      {/* Content */}
      <Box 
        style={{ flex: 1, marginBottom: '20px' }} 
        onClick={(e) => {
          e.stopPropagation();
          if (!isEditing) {
            setIsEditing(true);
          }
        }}
      >
        <NoteRichTextEditor
          content={localContent}
          onChange={setLocalContent}
          onBlur={handleBlur}
          onKeyDown={handleContentKeyDown}
          placeholder="Click to add content..."
          isEditing={isEditing}
          showToolbar={false}
          compactMode={true}
          noteElement={cardRef.current || undefined}
        />
      </Box>

      {/* Created date (shown on hover) */}
      {showCreatedDate && (
        <Box
          style={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            backgroundColor: 'color-mix(in srgb, var(--note-text-color) 10%, transparent)',
            border: '1px solid color-mix(in srgb, var(--note-text-color) 20%, transparent)',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '11px',
            color: 'var(--note-text-color)',
            animation: 'fadeIn 0.2s ease'
          }}
        >
          <Calendar size={10} />
          {formatDate(note.createdAt)}
        </Box>
      )}

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(2px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
      </Card>

      {/* Convert to Task Dialog */}
      <ConvertToTaskDialog
        isOpen={showConvertDialog}
        noteTitle={localTitle}
        noteContent={localContent}
        onClose={() => setShowConvertDialog(false)}
        onConvert={handleConvertToTask}
      />
    </>
  );
};

export default NoteCard;